import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';

/** Last material change — bump with any edit to the policy text. */
const LAST_UPDATED = '15 September 2026';

/**
 * Public privacy policy for the VON News and VON iPlayer apps, served at
 * `https://vonnews.ng/privacy` — no sign-in. This is the URL entered in App
 * Store Connect and the Play Console and linked from each app's More screen.
 *
 * Keep it in step with the store privacy disclosures (docs/PUBLISHING.md §5):
 * any new data an app collects must be added here before it ships.
 */
export default function PrivacyPolicy(): JSX.Element {
  return (
    <main className="min-h-screen bg-surface px-5 py-12">
      <article className="mx-auto w-full max-w-2xl text-[15px] leading-relaxed text-ink">
        <Logo size={36} withWordmark />

        <h1 className="mt-8 font-heading text-[32px] font-semibold leading-tight tracking-tight">
          Privacy policy
        </h1>
        <p className="mt-2 text-[13px] text-ink-muted">
          VON News and VON iPlayer apps · Last updated {LAST_UPDATED}
        </p>

        <P>
          The Voice of Nigeria Broadcasting Service (“VON”, “we”) publishes the VON News and
          VON iPlayer apps. This policy explains what information the apps collect, why, who
          we share it with, and the choices and rights you have. We process personal data in
          line with the Nigeria Data Protection Act 2023.
        </P>
        <P>
          You can read news, watch video and listen to radio without an account. Most of the
          information below is only collected if you choose to sign in or use a feature that
          needs it.
        </P>

        <H2>Information we collect</H2>
        <H3>Account information</H3>
        <P>
          If you create an account or sign in, we collect your email address and, if you give
          it, your name. If you sign in with Google (Android), we receive your name, email
          address and profile picture from Google. If you use a password, we store only a
          secure, one-way hash of it.
        </P>

        <H3>Things you create or submit</H3>
        <List>
          <li>
            <strong>Bookmarks and reactions</strong> you save to articles (VON News).
          </li>
          <li>
            <strong>Eyewitness reports</strong> (VON News): the text, photos and videos you
            choose to attach, and — only if you allow it — your precise location, so our
            editors can verify what happened. Reports are reviewed by editors and are not
            published automatically.
          </li>
          <li>
            <strong>Live contributions</strong> (VON iPlayer, staff correspondents only): the
            audio and video you broadcast when you tap Go Live, the title and any place name
            you type. Contributions are sent to the VON newsroom and may be recorded and
            broadcast. VON iPlayer does not use your device’s location.
          </li>
        </List>

        <H3>Usage information (VON News)</H3>
        <P>
          To understand how the app is used and improve it, VON News records app sessions,
          which articles are opened, listening sessions and taps on notifications. These
          records carry a random identifier created when the app is installed, your app
          version, device model, operating-system version and language, the approximate
          country derived from your network address, and your account ID if you are signed
          in. We do not use this information for advertising.
        </P>

        <H3>Notifications</H3>
        <P>
          If you allow notifications, we store a notification token for your device so we can
          send breaking-news alerts (VON News). VON iPlayer’s programme reminders are
          scheduled on your device and do not send us any data.
        </P>

        <H3>Crash and performance reports</H3>
        <P>
          When an app crashes or runs into an error, it sends a diagnostic report (for example
          the error, device model and operating-system version) so we can fix the problem.
        </P>

        <H3>What we don’t do</H3>
        <List>
          <li>We don’t sell your personal data.</li>
          <li>We don’t show third-party advertising or track you across other apps or websites.</li>
          <li>We don’t access your contacts, and we only use your camera, microphone, photos or location when you use a feature that needs them.</li>
        </List>

        <H2>How we use information</H2>
        <List>
          <li>To provide the apps: your account, bookmarks, reactions, reports and live contributions.</li>
          <li>To verify, edit and publish news, including eyewitness reports and live contributions.</li>
          <li>To send notifications you have asked for.</li>
          <li>To keep the service secure, prevent abuse and fix problems.</li>
          <li>To understand, in aggregate, what our audience reads, watches and listens to.</li>
        </List>

        <H2>Who we share it with</H2>
        <P>
          We use service providers who process data on our behalf, under contract and only for
          the purposes above:
        </P>
        <List>
          <li><strong>Google Firebase</strong> — push notifications and, on Android, crash reports.</li>
          <li><strong>Sentry</strong> — crash and error reports.</li>
          <li><strong>Google Sign-In</strong> — only if you choose to sign in with Google (Android).</li>
          <li><strong>Resend</strong> — sending sign-in, password-reset and account-deletion codes by email.</li>
          <li><strong>Cloud hosting and storage providers</strong> — running our servers and storing uploaded media and recordings.</li>
          <li><strong>YouTube</strong> — playing video; YouTube’s own privacy policy applies to video playback.</li>
        </List>
        <P>
          Some of these providers process data outside Nigeria, with safeguards required by the
          Nigeria Data Protection Act. We may also disclose information when required by law.
        </P>

        <H2>How long we keep it</H2>
        <P>
          We keep your account information and the things you create for as long as you have
          an account. When you delete your account, we delete it together with your
          bookmarks, reactions, eyewitness reports and their media, and your notification
          tokens. Content that has already been published as news may remain part of VON’s
          broadcast and editorial archive. Usage and diagnostic records are kept only as long
          as needed for the purposes above.
        </P>

        <H2>Deleting your account</H2>
        <P>
          You can delete your account at any time in either app (More → Delete account) or on
          the web at{' '}
          <Link to="/delete-account" className="text-brand hover:underline">
            vonnews.ng/delete-account
          </Link>
          . Reader accounts are deleted immediately. Staff accounts (correspondents and
          editors) are signed out straight away and removed by an administrator within 7 days,
          because they are linked to newsroom records.
        </P>

        <H2>Your rights</H2>
        <P>
          Under the Nigeria Data Protection Act 2023 you can ask to access, correct or delete
          your personal data, object to or restrict how we use it, and withdraw consent (for
          example, by turning off notifications or location access in your device settings).
          You can also complain to the Nigeria Data Protection Commission.
        </P>

        <H2>Children</H2>
        <P>
          The apps are for a general audience and are not directed at children under 13. We do
          not knowingly collect personal data from children under 13; if you believe a child
          has given us personal data, contact us and we will delete it.
        </P>

        <H2>Security</H2>
        <P>
          Data travels between the apps and our servers over encrypted connections. Sign-in
          tokens are kept in your device’s secure storage, and passwords are stored only as
          one-way hashes.
        </P>

        <H2>Changes</H2>
        <P>
          If we change this policy, we will update the date above and, for significant
          changes, tell you in the apps.
        </P>

        <H2>Contact us</H2>
        <P>
          Voice of Nigeria Broadcasting Service, Abuja, Nigeria. For privacy questions or to
          exercise your rights, contact us through{' '}
          <a href="https://von.gov.ng" className="text-brand hover:underline">
            von.gov.ng
          </a>
          .
        </P>
      </article>
    </main>
  );
}

function H2({ children }: { children: ReactNode }): JSX.Element {
  return <h2 className="mt-10 font-heading text-[22px] font-semibold tracking-tight">{children}</h2>;
}

function H3({ children }: { children: ReactNode }): JSX.Element {
  return <h3 className="mt-6 text-[15px] font-semibold">{children}</h3>;
}

function P({ children }: { children: ReactNode }): JSX.Element {
  return <p className="mt-3 text-ink-muted">{children}</p>;
}

function List({ children }: { children: ReactNode }): JSX.Element {
  return <ul className="mt-3 list-disc space-y-1.5 pl-5 text-ink-muted">{children}</ul>;
}
